<template>
    <div class="receipt-scanner" :class="{ 'receipt-scanner--page': standalone }">
        <header v-if="standalone" class="scan-hero">
            <div class="scan-hero__inner">
                <router-link to="/Profile" class="scan-hero__back">
                    <v-icon small color="white">mdi-arrow-left</v-icon> Το προφίλ μου
                </router-link>
                <h1>Σκανάρετε απόδειξη</h1>
                <p>Οι αποδείξεις από συνεργαζόμενες επιχειρήσεις καταχωρούνται στις συναλλαγές σας και στηρίζουν το σωματείο σας.</p>
            </div>
        </header>
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="d-none"
            @change="onFileSelected"
        >

        <!-- full screen camera, the camera is only active while this is open -->
        <div v-if="scanning" class="scanner-overlay">
            <video ref="video" class="scanner-video" playsinline muted></video>
            <div class="scanner-top">
                <span class="scanner-hint">Στοχεύστε το QR code της απόδειξης</span>
                <v-btn icon dark large @click="stopScan"> <v-icon large>mdi-close</v-icon> </v-btn>
            </div>
            <div class="scanner-bottom">
                <v-btn v-if="hasFlash" fab dark small color="rgba(255,255,255,0.2)" @click="toggleFlash">
                    <v-icon>{{ flashOn ? 'mdi-flashlight-off' : 'mdi-flashlight' }}</v-icon>
                </v-btn>
                <v-btn rounded dark color="rgba(255,255,255,0.2)" @click="stopScan(); pickFile()">
                    <v-icon class="mr-2">mdi-image</v-icon> Επιλογή φωτογραφίας
                </v-btn>
            </div>
            <div v-if="!cameraReady" class="scanner-starting">
                <v-progress-circular indeterminate color="white" />
            </div>
        </div>

        <div class="scan-body">
            <div v-if="processing" class="scan-panel scan-processing">
                <v-progress-circular indeterminate size="56" width="5" color="#1b2f5a" />
                <div class="scan-processing__text">{{ processingText }}</div>
                <div class="scan-processing__hint">Παρακαλώ μην κλείσετε τη σελίδα.</div>
            </div>

            <template v-else>
                <div v-if="result" class="scan-panel scan-result">
                    <div class="scan-result__icon" :style="{ background: tint(resultView.color) }">
                        <v-icon size="40" :color="resultView.color">{{ resultView.icon }}</v-icon>
                    </div>
                    <div class="scan-result__title" :style="{ color: resultView.color }">{{ resultView.title }}</div>
                    <div v-if="resultView.text" class="scan-result__text">{{ resultView.text }}</div>
                    <dl v-if="hasReceiptDetails" class="scan-result__details">
                        <div><dt>Ημερομηνία</dt><dd>{{ formatDate(result.data.date) }}</dd></div>
                        <div><dt>Επωνυμία</dt><dd>{{ result.data.name || '-' }}</dd></div>
                        <div><dt>Α.Φ.Μ.</dt><dd>{{ result.data.vat || '-' }}</dd></div>
                        <div><dt>Ποσό</dt><dd>{{ result.data.total ? result.data.total + ' €' : '-' }}</dd></div>
                    </dl>
                </div>

                <div class="scan-options">
                    <button type="button" class="scan-option" @click="startScan">
                        <span class="scan-option__icon"><v-icon size="30" color="#ad282d">mdi-qrcode-scan</v-icon></span>
                        <span class="scan-option__title">{{ result ? 'Σκανάρετε νέα απόδειξη' : 'Σκανάρετε το QR' }}</span>
                        <span class="scan-option__text">Για αποδείξεις με QR code. Ελέγχεται αυτόματα στην ΑΑΔΕ.</span>
                        <span class="scan-option__cta">Άνοιγμα κάμερας <v-icon small color="#ad282d">mdi-arrow-right</v-icon></span>
                    </button>
                    <button type="button" class="scan-option" @click="pickFile">
                        <span class="scan-option__icon"><v-icon size="30" color="#ad282d">mdi-camera-outline</v-icon></span>
                        <span class="scan-option__title">Ανεβάστε φωτογραφία</span>
                        <span class="scan-option__text">Για αποδείξεις ή τιμολόγια χωρίς QR. Ελέγχονται από την ομάδα μας.</span>
                        <span class="scan-option__cta">Επιλογή φωτογραφίας <v-icon small color="#ad282d">mdi-arrow-right</v-icon></span>
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
    import QrScanner from 'qr-scanner'
    import qrReaderService from "@/services/qr-reader"
    import transactionService from "@/services/transaction"

    // longest side of an uploaded photo, enough for a receipt to stay legible
    const MAX_UPLOAD_DIMENSION = 2000
    const UPLOAD_JPEG_QUALITY = 0.85

    const RESULT_VIEWS = {
        success: { icon: 'mdi-check-circle', color: '#2e7d32', title: 'Η απόδειξη έχει περαστεί στις συναλλαγές σας' },
        pending: {
            icon: 'mdi-clock-outline', color: '#1b2f5a', title: 'Η απόδειξη έχει περαστεί στις συναλλαγές σας',
            text: 'Τα στοιχεία της εκκρεμούν από την ΑΑΔΕ και θα ενημερωθούν αυτόματα.'
        },
        duplicate: { icon: 'mdi-content-copy', color: '#ae262e', title: 'Η συγκεκριμένη απόδειξη έχει σκαναριστεί ήδη' },
        'not-partner': {
            icon: 'mdi-store-remove-outline', color: '#ae262e', title: 'Ο εκδότης της απόδειξης δεν είναι συνεργάτης της paron card',
            text: 'Η απόδειξη δεν καταχωρήθηκε.'
        },
        'invalid-qr': {
            icon: 'mdi-qrcode-remove', color: '#ae262e', title: 'Το QR code δεν αντιστοιχεί σε απόδειξη',
            text: 'Δοκιμάστε να σκανάρετε το QR code της απόδειξης ή ανεβάστε φωτογραφία της.'
        },
        uploaded: {
            icon: 'mdi-check-circle', color: '#2e7d32', title: 'Η απόδειξη ανέβηκε επιτυχώς!',
            text: 'Θα ελεγχθεί και θα περαστεί στις συναλλαγές σας.'
        },
        'camera-denied': {
            icon: 'mdi-camera-off', color: '#ae262e', title: 'Δεν δόθηκε άδεια χρήσης της κάμερας',
            text: 'Επιτρέψτε την πρόσβαση στην κάμερα από τις ρυθμίσεις του browser ή ανεβάστε φωτογραφία της απόδειξης.'
        },
        'camera-error': {
            icon: 'mdi-camera-off', color: '#ae262e', title: 'Δεν ήταν δυνατή η χρήση της κάμερας',
            text: 'Μπορείτε να ανεβάσετε φωτογραφία της απόδειξης.'
        },
        'upload-error': {
            icon: 'mdi-alert-circle', color: '#ae262e', title: 'Η αποστολή της φωτογραφίας απέτυχε',
            text: 'Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.'
        },
        error: {
            icon: 'mdi-alert-circle', color: '#ae262e', title: 'Δεν ήταν δυνατή η ανάγνωση της απόδειξης',
            text: 'Μπορεί η απόδειξη να είναι δυσανάγνωστη ή η ΑΑΔΕ να μην απαντά. Παρακαλώ αποστείλετε φωτογραφία της απόδειξης/του τιμολογίου.'
        }
    }

    export default {
        data: () => ({
            scanning: false,
            cameraReady: false,
            hasFlash: false,
            flashOn: false,
            processing: false,
            processingText: '',
            result: null
        }),
        computed: {
            // card number and initials of the logged-in member: the backend checks them before crediting a receipt
            cardProof() {
                return {
                    card: this.$store.getters.getCard.card,
                    initials: this.$store.getters.getInitials.initials
                }
            },
            memberId() {
                const paronMember = this.$store.getters.getParonMember
                return paronMember ? paronMember.member.id : null
            },
            // the /qr page has its own header; inside the profile the panel already has a title
            standalone() {
                return this.$route.name === 'QR'
            },
            resultView() {
                return this.result ? RESULT_VIEWS[this.result.type] : null
            },
            hasReceiptDetails() {
                return this.result && this.result.data && (this.result.data.total || this.result.data.name)
            }
        },
        created() {
            if(!this.$store.getters.getParonMember) {
                this.$router.push({name: 'Login'}).catch(() => {})
            }
        },
        beforeDestroy() {
            this.destroyScanner()
        },
        methods: {
            // light background in the colour of the result icon
            tint(color) {
                return /^#[0-9a-f]{6}$/i.test(color || '') ? `${color}1a` : 'rgba(27, 47, 90, 0.08)'
            },
            async startScan() {
                this.result = null
                this.cameraReady = false
                this.scanning = true
                document.documentElement.classList.add('scanner-open')
                document.addEventListener('keydown', this.onKeydown)
                await this.$nextTick()

                this.scanner = new QrScanner(this.$refs.video, (scan) => this.onDecode(scan.data), {
                    preferredCamera: 'environment',
                    returnDetailedScanResult: true,
                    highlightScanRegion: true,
                    highlightCodeOutline: true,
                    maxScansPerSecond: 15,
                    // receipt qr codes are small and dense, so scan a bigger area at a higher resolution than the default
                    calculateScanRegion: (video) => {
                        const size = Math.round(Math.min(video.videoWidth, video.videoHeight) * 0.8)
                        const downScaledSize = Math.min(size, 800)
                        return {
                            x: Math.round((video.videoWidth - size) / 2),
                            y: Math.round((video.videoHeight - size) / 2),
                            width: size,
                            height: size,
                            downScaledWidth: downScaledSize,
                            downScaledHeight: downScaledSize
                        }
                    }
                })
                try {
                    await this.scanner.start()
                    this.cameraReady = true
                    this.hasFlash = await this.scanner.hasFlash()
                } catch (error) {
                    console.error('QR Scanner Error:', error)
                    this.stopScan()
                    const denied = error && (error.name === 'NotAllowedError' || error.name === 'SecurityError')
                    this.result = { type: denied ? 'camera-denied' : 'camera-error' }
                }
            },
            stopScan() {
                this.destroyScanner()
                this.scanning = false
                this.flashOn = false
                document.documentElement.classList.remove('scanner-open')
                document.removeEventListener('keydown', this.onKeydown)
            },
            destroyScanner() {
                if (this.scanner) {
                    this.scanner.destroy()
                    this.scanner = null
                }
            },
            onKeydown(event) {
                if (event.key === 'Escape') {
                    this.stopScan()
                }
            },
            async toggleFlash() {
                await this.scanner.toggleFlash()
                this.flashOn = this.scanner.isFlashOn()
            },
            onDecode(decodedString) {
                if (!this.scanning || this.processing) {
                    return
                }
                this.stopScan()
                if (navigator.vibrate) {
                    navigator.vibrate(80)
                }
                this.submitReceiptUrl(decodedString)
            },
            async submitReceiptUrl(url) {
                if (!/^https?:\/\//i.test(url.trim())) {
                    this.result = { type: 'invalid-qr' }
                    return
                }
                this.processing = true
                this.processingText = 'Γίνεται έλεγχος της απόδειξης στην ΑΑΔΕ...'
                try {
                    const response = await qrReaderService.getReceiptInfo({ 'url': url.trim(), 'memberId': this.memberId, ...this.cardProof })
                    this.result = this.parseExtractResponse(response)
                } catch (error) {
                    const body = error.response && error.response.data
                    if (body && body.data && body.data.message === 'not a partner') {
                        this.result = { type: 'not-partner', data: body.data }
                    } else {
                        this.result = { type: 'error' }
                    }
                } finally {
                    this.processing = false
                }
                if (this.result.type === 'success' || this.result.type === 'pending') {
                    this.$emit('transaction-created')
                }
            },
            parseExtractResponse(response) {
                const data = response && response.data
                if (!data || response['400']) {
                    return { type: 'error' }
                }
                if (data.message === 'duplicate receipt') {
                    return { type: 'duplicate', data }
                }
                return { type: data.scanCompleted ? 'success' : 'pending', data }
            },
            pickFile() {
                this.$refs.fileInput.click()
            },
            async onFileSelected(event) {
                const file = event.target.files[0]
                // reset so that picking the same photo again still triggers a change
                event.target.value = ''
                if (!file) {
                    return
                }
                this.result = null
                this.processing = true
                this.processingText = 'Ανάγνωση φωτογραφίας...'

                // a photo that contains the receipt qr code gets the full AADE data, like a live scan
                const url = await this.readQrFromImage(file)
                if (url) {
                    await this.submitReceiptUrl(url)
                    if (this.result.type !== 'error') {
                        return
                    }
                    this.result = null
                    this.processing = true
                }
                await this.uploadImage(file)
            },
            async readQrFromImage(file) {
                try {
                    const scan = await QrScanner.scanImage(file, { returnDetailedScanResult: true, alsoTryWithoutScanRegion: true })
                    return /^https?:\/\//i.test(scan.data.trim()) ? scan.data : null
                } catch (error) {
                    return null
                }
            },
            async uploadImage(file) {
                this.processingText = 'Αποστολή φωτογραφίας...'
                try {
                    const formData = new FormData()
                    const upload = await this.compressImage(file)
                    formData.append('file', upload, upload.name)
                    formData.append('member', this.memberId)
                    formData.append('card', this.cardProof.card)
                    formData.append('initials', this.cardProof.initials)
                    const response = await transactionService.createTransaction(formData)
                    if (!response || !response.image_url) {
                        throw new Error('image upload failed')
                    }
                    this.result = { type: 'uploaded' }
                    this.$emit('transaction-created')
                } catch (error) {
                    console.error('Receipt upload error:', error)
                    this.result = { type: 'upload-error' }
                } finally {
                    this.processing = false
                }
            },
            // phone photos are often several MB, downscale them so the upload is fast and stays under the server limit
            async compressImage(file) {
                let image
                try {
                    image = await this.loadImage(file)
                } catch (error) {
                    // formats the browser cannot decode (e.g. heic outside safari) are sent as they are
                    return file
                }
                const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(image.width, image.height))
                if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
                    return file
                }
                const canvas = document.createElement('canvas')
                canvas.width = Math.round(image.width * scale)
                canvas.height = Math.round(image.height * scale)
                canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)
                const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', UPLOAD_JPEG_QUALITY))
                if (!blob || blob.size >= file.size) {
                    return file
                }
                const name = (file.name || 'receipt').replace(/\.[^.]+$/, '') + '.jpg'
                return new File([blob], name, { type: 'image/jpeg' })
            },
            loadImage(file) {
                return new Promise((resolve, reject) => {
                    const objectUrl = URL.createObjectURL(file)
                    const image = new Image()
                    image.onload = () => {
                        URL.revokeObjectURL(objectUrl)
                        resolve(image)
                    }
                    image.onerror = () => {
                        URL.revokeObjectURL(objectUrl)
                        reject(new Error('image could not be decoded'))
                    }
                    image.src = objectUrl
                })
            },
            formatDate(date) {
                if (!date) {
                    return '-'
                }
                const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date)
                return match ? `${match[3]}/${match[2]}/${match[1]}` : date
            }
        }
    }
</script>

<style scoped>
.scanner-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1000;
    background: #000;
}

.scanner-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.scanner-top,
.scanner-bottom {
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    z-index: 2;
}

.scanner-top {
    top: 0;
    justify-content: space-between;
    background: linear-gradient(rgba(0, 0, 0, 0.6), transparent);
}

.scanner-bottom {
    bottom: 0;
    justify-content: center;
    gap: 16px;
    padding-bottom: 32px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.6));
}

.scanner-hint {
    color: #fff;
    font-size: 16px;
}

.scanner-starting {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.scanner-overlay >>> .scan-region-highlight-svg,
.scanner-overlay >>> .code-outline-highlight {
    stroke: #ae262e !important;
}

.receipt-scanner {
    color: #1b2f5a;
}

.receipt-scanner--page {
    padding-bottom: 64px;
    background: #f6f7fa;
}

.scan-hero {
    padding: 32px 18px 80px;
    background: linear-gradient(135deg, #1b2f5a 0%, #2c4a86 100%);
    color: #fff;
}

.scan-hero__inner,
.receipt-scanner--page .scan-body {
    max-width: 820px;
    margin: 0 auto;
}

.scan-hero__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.85) !important;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
}

.scan-hero h1 {
    margin: 14px 0 6px;
    font-size: clamp(26px, 4vw, 36px);
    font-weight: 700;
}

.scan-hero p {
    max-width: 560px;
    margin: 0;
    color: rgba(255, 255, 255, 0.85);
}

.receipt-scanner--page .scan-body {
    margin-top: -52px;
    padding: 0 18px;
}

.scan-panel {
    margin-bottom: 16px;
    padding: 28px 24px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 18px;
    box-shadow: 0 8px 24px rgba(27, 47, 90, 0.08);
    text-align: center;
}

.scan-processing__text {
    margin-top: 16px;
    font-size: 17px;
    font-weight: 600;
}

.scan-processing__hint {
    margin-top: 4px;
    font-size: 14px;
    color: rgba(27, 47, 90, 0.65);
}

.scan-result__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 50%;
}

.scan-result__title {
    margin-top: 12px;
    font-size: 20px;
    font-weight: 700;
}

.scan-result__text {
    max-width: 460px;
    margin: 6px auto 0;
    color: rgba(27, 47, 90, 0.75);
}

.scan-result__details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 460px;
    margin: 20px auto 0;
    text-align: left;
}

.scan-result__details > div {
    padding: 10px 14px;
    border-radius: 12px;
    background: #f6f7fa;
}

.scan-result__details dt {
    font-size: 12px;
    color: rgba(27, 47, 90, 0.65);
}

.scan-result__details dd {
    margin: 0;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.scan-options {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.scan-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 24px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.12);
    border-radius: 18px;
    box-shadow: 0 8px 24px rgba(27, 47, 90, 0.08);
    color: #1b2f5a;
    text-align: left;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.scan-option:hover,
.scan-option:focus-visible {
    transform: translateY(-3px);
    border-color: rgba(173, 40, 45, 0.35);
    box-shadow: 0 14px 30px rgba(27, 47, 90, 0.14);
}

.scan-option__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 6px;
    border-radius: 16px;
    background: rgba(173, 40, 45, 0.08);
}

.scan-option__title {
    font-size: 18px;
    font-weight: 700;
}

.scan-option__text {
    flex: 1;
    font-size: 14px;
    line-height: 1.5;
    color: rgba(27, 47, 90, 0.7);
}

.scan-option__cta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    color: #ad282d;
    font-size: 14px;
    font-weight: 700;
}

@media (max-width: 599px) {
    .scan-hero {
        padding: 24px 18px 72px;
    }

    .scan-options {
        grid-template-columns: 1fr;
    }

    .scan-option {
        padding: 18px;
    }
}
</style>

<style>
/* stop the page behind the camera from scrolling */
html.scanner-open {
    overflow: hidden !important;
}
</style>
